/* ---------------------------------------------------------------------------
   The app page's status bar.

   The file kept its name from the structures pane that used to live here too:
   the register as a list down the left of a structure's screen. It came off
   every screen — the record's inventory column took its place, and the register
   and the title bar's search are the ways to the next structure.

   THE STATUS BAR is the foot of the app page: whether this device is talking to
   the register, what is waiting to go, whose register it is, which
   structure is in hand and which of its 3D models is on screen — the facts that are true of the page rather than of the
   screen, in the place a desktop application keeps them.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import type { State } from '../core/store.ts';
import { findBridge, heroModel } from '../data/derive.ts';
import { dashboardHref } from '../core/navigate.ts';
import { agency } from '../tenant/agency.ts';
import type { Screen } from '../data/types.ts';

/** The screens whose subject is one structure, whose id the status bar names. */
const STRUCTURE_SCREENS: ReadonlySet<Screen> = new Set<Screen>(['detail', 'report', 'sheet', 'schedule', 'site']);

function connection(s: Readonly<State>): { text: string; tone: 'good' | 'warn' | 'idle' } {
  if (s.queued.length) {
    const n = s.queued.length;
    return { text: s.syncing ? `Syncing ${n}…` : `${n} change${n === 1 ? '' : 's'} waiting to sync`, tone: 'warn' };
  }
  switch (s.backend) {
    case 'live':
      return { text: 'Online — all changes synced', tone: 'good' };
    case 'unreachable':
      return { text: 'Offline — showing the bundled register', tone: 'warn' };
    default:
      return { text: 'Connecting…', tone: 'idle' };
  }
}

export function statusbar(s: Readonly<State>): HTMLElement {
  const c = connection(s);
  const b = STRUCTURE_SCREENS.has(s.screen) ? findBridge(s.selectedId) : null;
  const block = agency();
  const model = b !== null && s.screen === 'detail' ? heroModel(s.models.models) : null;
  return h(
    'footer',
    { class: 'appstatus', 'aria-label': 'Status' },
    h(
      'span',
      { class: 'appstatus__item', title: s.backendNote || c.text },
      h('span', { class: ['appstatus__dot', `appstatus__dot--${c.tone}`] }),
      c.text,
    ),
    h('span', { class: 'appstatus__item', text: `${block.department} · ${block.branch}` }),
    h('span', { class: 'spacer' }),
    /* The structure in hand, and the way to its dashboard (LC-30). */
    b
      ? h('a', { class: 'appstatus__item', href: dashboardHref(s, b.id), title: `The dashboard of ${b.name}`, text: b.id })
      : null,
    /* The 3D model on screen, by name — the record's, while it is showing. */
    model ? h('span', { class: 'appstatus__item', title: 'The 3D model on screen', text: model.name }) : null,
  );
}
